import React, { useState, useEffect } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { useEnroll } from '../context/EnrollContext';
import { BATCH_TARGET } from '../data/courseData';
import { submitEnrollment } from '../utils/submitEnroll';
import styles from './EnrollModal.module.css';

export default function EnrollModal() {
  const { isOpen, close } = useEnroll();
  const cd = useCountdown(BATCH_TARGET);
  const [form, setForm]       = useState({ name: '', phone: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);
    setFormError('');
    try {
      await submitEnrollment({ ...form, source: 'modal' });
      setSubmitted(true);
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Enroll in Performance Marketing Course"
    >
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.close} onClick={close} aria-label="Close modal">×</button>

        <div className={styles.cardTop} />
        <span className={styles.badge}>🎯 Next Batch — Filling Fast</span>
        <h2 className={styles.title}>Claim Your Seat Now</h2>
        <p className={styles.sub}>Get a free counselling call + detailed curriculum PDF</p>

        <div className={styles.countdownSection}>
          <p className={styles.countdownLabel}>Batch starts in</p>
          <div className={styles.countdown}>
            {[
              { val: cd.days,    lbl: 'Days' },
              { val: cd.hours,   lbl: 'Hrs' },
              { val: cd.minutes, lbl: 'Min' },
              { val: cd.seconds, lbl: 'Sec' },
            ].map(u => (
              <div className={styles.cdUnit} key={u.lbl}>
                <span className={styles.cdNum}>{u.val}</span>
                <span className={styles.cdLbl}>{u.lbl}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.seatsBar}>
          <div className={styles.seatsLabel}>
            <span>Seats Filling</span>
            <strong>78% Full</strong>
          </div>
          <div className={styles.barTrack}>
            <div className={styles.barFill} />
          </div>
        </div>

        {submitted ? (
          <div className={styles.successMsg}>
            <span className={styles.successIcon}>✅</span>
            <strong>You're on the list!</strong>
            <p>We'll call you within 4 hours to confirm your seat.</p>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <input
              type="text"
              className={styles.input}
              placeholder="Your Full Name *"
              aria-label="Full Name"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              required
            />
            <input
              type="tel"
              className={styles.input}
              placeholder="Phone / WhatsApp Number *"
              aria-label="Phone or WhatsApp Number"
              value={form.phone}
              onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
              required
            />
            <input
              type="email"
              className={styles.input}
              placeholder="Email Address (optional)"
              aria-label="Email Address"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            />
            <button type="submit" className={styles.enrollBtn} disabled={loading}>
              {loading ? 'Submitting…' : 'Book My Free Counselling Call 📞'}
            </button>
            {formError && <p className={styles.formError}>{formError}</p>}
            <p className={styles.privacy}>🔒 Your info is private. No spam, ever.</p>
          </form>
        )}
      </div>
    </div>
  );
}
